/**
 * One frame of pilot input: held keys, a pointer and a joystick in, one tick's
 * `FlightInputs` out, with the two *carried* controls advanced on the way
 * through.
 *
 * Two of the four axes have a position that persists between frames rather than
 * a value the keys read straight off, and both are levers a hand would be
 * holding:
 *
 * - **the throttle**, which travels idle-to-full in two seconds (`keys.ts`'s
 *   `advanceThrottle`), and
 * - **the stick** (AD-007n change 4) — `RB.EXE`'s keyboard is a ramped analogue
 *   axis, about a second and a half to full deflection and a second of roll-out
 *   after the key comes up, where the port's was bang-bang and so made every
 *   input a full-deflection input. `packages/engine`'s `advanceStick` does the
 *   shaping; "instant" is an option and not the default (`stick-settings.ts`).
 *
 * A joystick joins here and nowhere else, which is the whole of its integration
 * with the sim. Deciding *which* device owns an axis is `gamepad-blend.ts`'s
 * job and is pure; what matters at this seam is that a stick's reading arrives
 * on the same line the keyboard's does and leaves through the same
 * `quantizeInputs`.
 *
 * The result is **quantised before the sim steps on it** rather than on the way
 * into a tape: a flight simulated on a raw `0.7333333333333333` and recorded as
 * `0.733` replays as a different flight from tick one, with no visible cause.
 * AD-008's rule for the duel wire, applied at the same place.
 *
 * That rule is what makes analogue input safe here, and it is worth being exact
 * about why. A keyboard produces −1, 0 and +1 and a ramp between them; an axis
 * produces whatever a potentiometer and four floating-point operations
 * (calibration, dead zone, curve, clamp) happen to land on, which is a number
 * with no reason to be the same on two machines. It never has to be. Every
 * consumer that could disagree — the duel wire, the Mission Recorder's tape,
 * the replay — reads the value *after* this line, and this line rounds it to a
 * thousandth of full deflection with the engine's own `quantizeInputs`, the
 * same function and the same arithmetic (`round(v · 1000) / 1000`) that
 * `net/protocol.ts` and `replay/input-trace.ts` spell. A peer never recomputes
 * the other peer's transform; it receives the quantised result. So a joystick
 * flight is reproducible from its input ledger for exactly the reason a
 * keyboard flight is, and `/duel`'s hashes cannot part company over a
 * potentiometer.
 *
 * The wheel's own routing — throttle everywhere, fly-by zoom in that one camera
 * mode — is `scene-flyby-zoom.ts`'s `routeWheelForFlyByZoom`, which returns zero
 * here whenever the wheel just went to the camera instead, so the sim (and any
 * tape recorded from this frame) never sees a throttle change that was actually
 * a zoom.
 */

import { advanceStick, quantizeInputs, type FlightInputs, type StickAxes, type StickShaping } from "@red-opus/engine";
import { NO_PAD_INPUT, type PadFrame } from "./gamepad-axes.ts";
import {
  blendStick,
  keyStickActive,
  padStickDeflected,
  padThrottleTravel,
  resolveStickSource,
  resolveThrottle,
  type InputSource,
} from "./gamepad-blend.ts";
import { advanceThrottle, flightInputsFrom, stickDemandFrom, THROTTLE_TRAVEL_SECONDS } from "./keys.ts";
import type { PointerFrame } from "./pointer.ts";

/** The two carried lever positions, mutated in place by `readFlightInputs`. */
export interface CarriedControls {
  throttle: number;
  stick: StickAxes;
  stickShaping: StickShaping;
  /** Which device last spoke for the stick — carried, because "neither is touching it" has to keep its answer. */
  stickSource: InputSource;
}

export function readFlightInputs(
  controls: CarriedControls,
  pressed: ReadonlySet<string>,
  pointer: PointerFrame,
  dt: number,
  wheelDelta: number,
  pad: PadFrame = NO_PAD_INPUT,
): FlightInputs {
  // The stick's shoulder buttons travel the lever at the same rate `+` and `-`
  // do, so they are summed in as one more wheel-shaped contribution rather than
  // becoming a second throttle with a feel of its own.
  const padTravel = padThrottleTravel(pad.held, dt, THROTTLE_TRAVEL_SECONDS);
  controls.throttle = resolveThrottle(advanceThrottle(controls.throttle, pressed, dt, wheelDelta + padTravel), pad.axes.throttle);

  const demand = stickDemandFrom(pressed);
  const keyStick = advanceStick(controls.stick, demand, dt, controls.stickShaping);
  controls.stickSource = resolveStickSource(
    controls.stickSource,
    keyStickActive(demand),
    padStickDeflected(pad.axes),
    pad.device !== undefined,
  );
  // Written back whichever source won: a stick unplugged at full deflection
  // leaves the carried position there for `advanceStick` to fly home.
  controls.stick = blendStick(controls.stickSource, keyStick, pad.axes);

  const inputs = flightInputsFrom(pressed, controls.stick, controls.throttle, pointer.firing || pad.held.has("fire"));
  return quantizeInputs({
    ...inputs,
    clearJam: inputs.clearJam || pad.held.has("clearJam"),
    bombRelease: inputs.bombRelease || pad.held.has("bombRelease"),
  });
}
